/**
 * The locale this page shows (integration plan slice 5): `?lang=en|zh-Hans|ja`
 * (any letter case) overrides; else the language the player chose on the
 * picker, remembered in `localStorage` (`ab.lang`; `rememberLocale` is called
 * only on a choice, like `rememberTier`); else the browser's languages in
 * order of preference (`ja*` is Japanese; `zh*` is Simplified Chinese except
 * Traditional Chinese, `zh-Hant*`, `zh-TW`, `zh-HK` and `zh-MO`, which is
 * skipped: it is not shipped and Simplified must not be forced on its
 * readers, so the next language decides), else English.
 */
import { type Locale, locales, sourceLocale } from '@ab/session';

const storageKey = 'ab.lang';

export const isLocale = (value: string | null): value is Locale => value !== null && (locales as readonly string[]).includes(value);

/** A locale name in any letter case (`zh-hans`, `JA`) as the game's spelling; null when it is none of them. */
export const localeNamed = (value: string | null): Locale | null => (value === null ? null : (locales.find((l) => l.toLowerCase() === value.toLowerCase()) ?? null));

/** Traditional Chinese tags: a script subtag `Hant`, or a region that writes Traditional by default. */
const traditionalChinese = /^zh-(hant|tw|hk|mo)(-|$)/;

/**
 * A browser language tag (`ja-JP`, `zh-CN`, `en-GB`) as one of the game's locales; null when the game has none
 * for it, Traditional Chinese included (`zh-TW`, `zh-Hant-HK`: skipped, so the next preferred language decides).
 */
export const localeOfTag = (tag: string): Locale | null => {
  const lower = tag.toLowerCase();
  if (traditionalChinese.test(lower)) return null;
  if (lower === 'zh' || lower.startsWith('zh-')) return 'zh-Hans';
  if (lower === 'ja' || lower.startsWith('ja-')) return 'ja';
  if (lower === 'en' || lower.startsWith('en-')) return 'en';
  return null;
};

export const chooseLocale = (params: URLSearchParams, languages: readonly string[]): Locale => {
  const asked = localeNamed(params.get('lang'));
  if (asked !== null) return asked;
  try {
    const saved = localStorage.getItem(storageKey);
    if (isLocale(saved)) return saved;
  } catch {
    // Storage can be unavailable (a private window); the browser's languages decide then.
  }
  for (const tag of languages) {
    const locale = localeOfTag(tag);
    if (locale !== null) return locale;
  }
  return sourceLocale;
};

/** Remembers a locale the player chose on the picker (never one the browser's languages picked). */
export const rememberLocale = (locale: Locale): void => {
  try {
    localStorage.setItem(storageKey, locale);
  } catch {
    // Not remembered; the next visit asks the browser's languages again.
  }
};
